import { action } from '@ember/object';
import type { Registry as Services } from '@ember/service';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { PrismButton, PrismTooltip } from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { not } from 'ember-truth-helpers';
import type { User } from 'frontend-utils/models';
import type { Userproductaccess } from 'lumen/models';

import styles from './cell-cta.module.css';

interface SettingsHotelAccessTablesCellsCellCtaSignature {
  Args: {
    row: User & { doRefresh: (shouldReload: boolean) => void };
  };
}

export default class SettingsHotelAccessTablesCellsCellCta extends Component<SettingsHotelAccessTablesCellsCellCtaSignature> {
  @service declare ajax: Services['ajax'];
  @service declare alerting: Services['alerting'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare router: Services['router'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get editRoute() {
    if (this.hotelAndMarketService.hotel?.id) {
      return 'hotel.settings.hotels.edit';
    }
    if (this.hotelAndMarketService.market?.id) {
      return 'market.settings.hotels.edit';
    }
    return 'destination.settings.hotels.edit';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get user() {
    return this.args.row;
  }

  /**
   * Remove user from all hotels I have access to
   */

  @action
  async deleteUser(): Promise<void> {
    // Go over every userproductaccess and count all hotels they have access to for each product
    const hotelsCount = (
      this.user.userproductaccess as unknown as Userproductaccess[]
    ).reduce((total, current) => total + current.count, 0);
    const confirmWasClicked = await this.alerting.showConfirmWarningAlert({
      title: this.intl.t('settings.hotel_access.delete_user'),
      text: this.intl.t('settings.hotel_access.remove_user', {
        count: hotelsCount,
      }),
    });

    if (confirmWasClicked) {
      // Use the crud API to delete all UHRs for this user of which the current logged-in user has admin access to
      // All UHRs are selected by using the exclude function and not excluding any subscription id
      this.ajax
        .request('userhotelrelationships/', {
          method: 'DELETE',
          data: {
            user_id: this.user?.id,
            exclude: true,
            subscription_ids: [],
          },
        })
        .then(() => {
          this.args.row.doRefresh(true);
        })
        .catch(() => {
          void this.alerting.showErrorAlert({
            title: this.intl.t('settings.hotel_access.update_failed'),
          });
        });
    }
  }

  @action editUser(): void {
    this.router.transitionTo(this.editRoute, {
      queryParams: { userId: this.user.id },
    });
  }

  <template>
    {{#if this.user.id}}
      <div class={{styles.hotel-access-cta}}>
        <PrismButton
          @buttonType="ghost"
          @icon="edit-pencil"
          @onClick={{this.editUser}}
          @size="small"
          data-test-id="hotel-access-edit-button"
        />

        <div>
          <PrismButton
            @buttonType="ghost"
            @icon="bin"
            @isDisabled={{not this.user.isDeletable}}
            @onClick={{this.deleteUser}}
            @size="small"
            data-event-id="hotel-settings-hotels-delete-user"
            data-event-value={{this.user.isDeletable}}
          />
          {{#unless this.user.isDeletable}}
            <PrismTooltip
              @side="left"
              @text={{t "settings.hotels.user_no_delete_tooltip"}}
            />
          {{/unless}}
        </div>
      </div>
    {{/if}}
  </template>
}
